import { useClient } from './ClientContext';
import { useGlobals } from './GlobalContext';
import { useCart } from './CartContext';
import { useDiscountFlex } from './DiscountFlexContext';
import { useMemo, useContext, createContext, useCallback } from 'react';
import { useUserContext } from './userContext/UserContext'; // <== novo
import { getOnStorage } from '@/shared';
import { useCorporateFlags } from './CorporateFlagsContext';
import { useComboCartItems } from '@/talons/combos/useComboCartItems';
import { buildComboDisplaySplitsForCartLine } from '@/talons/combos/comboCartUtils';

interface HookProps {
  // Consumo (apenas DESCONTOS: percentuais negativos) -> impacta saldo ERP
  discountAmount: number;
  discountAmountClient: number;
  getDiscountAmountByClient: (client: number) => number;

  // Novos: valores para exibição no carrinho
  increasesClient: number; // Somente acréscimos (saldo a faturar) do cliente atual
  totalFlexClient: number; // discountAmountClient (negativo) + increasesClient (positivo)
  discountAmountAll: number; // Para todos os clientes (caso seja preciso em outro lugar)

  // Novos: saldos estimados (não persistidos)
  estimatedSaldoFlex: number; // saldo base + descontos (todos clientes)
  estimatedSaldoFlexClient: number; // saldo base + descontos cliente atual

  // Novos aliases (usados para leitura mais semântica em outros componentes)
  currentSaldoFlex?: number;
  currentSaldoFlexClient?: number;
}

const DiscountFlexAmountContext = createContext<HookProps>({} as HookProps);

const DiscountFlexAmountProvider: React.FC<any> = ({ children }) => {
  const { cartDetails } = useCart();
  const { paymentCode } = useGlobals();
  const { selectedClient } = useClient();
  const { discounts } = useDiscountFlex();
  const { getComboCartItemInfo } = useComboCartItems();
  const { rcaSaldoFlex } = useUserContext(); // saldo base persistido (ainda sem consumo do carrinho)
  const { isBFSeller } = useCorporateFlags();
  const baseSaldo = rcaSaldoFlex || 0;

  // Recupera preço original a partir do final + percentual negativo
  const getOriginalValue = (finalValue: number, percent: number) => {
    if (percent >= 0) return finalValue; // acréscimo ou neutro não reconstrói “desconto”
    const d = percent / 100;
    const fator = 1 + d;
    if (fator <= 0) return finalValue;
    return finalValue / fator;
  };

  // A chave de cada desconto salvo é o mountKey inteiro (produto-embalagem-qtdEmb-uid, ver
  // DiscountFlexContext), não o uid da linha — e o uid embutido nele já é escopado por porção
  // de combo (uid-comboInstanceId / uid-excess). Reconstruímos o mesmo mountKey por porção,
  // por cliente, para achar a quantidade certa a multiplicar.
  const quantityByMountKey = useMemo(() => {
    if (!cartDetails) return {};

    const selectedClientCod = selectedClient?.COD_CLIENTE ? `${selectedClient.COD_CLIENTE}` : null;

    return Object.entries(cartDetails).reduce((acc, [cartKey, cartDetail]) => {
      if (!cartDetail?.items?.length) return acc;

      const [clientKey] = `${cartKey}`.split(':');
      if (!clientKey) return acc;

      // Info de combo só está disponível para o carrinho do cliente selecionado no momento.
      const getAllocations = clientKey === selectedClientCod ? getComboCartItemInfo : undefined;

      cartDetail.items.forEach((item) => {
        const allocations = getAllocations?.(item.uid);
        const slices = buildComboDisplaySplitsForCartLine(item, allocations);
        const [packOption, packQtyStr] = (item.option_uid || '').split('_');
        const packQty = Number(packQtyStr) || 0;

        slices.forEach((slice) => {
          acc[`${clientKey}-${item.sku}-${packOption}-${packQty}-${slice.displayKey}`] = slice.displayQuantity || 0;
        });
      });

      return acc;
    }, {} as { [key: string]: number });
  }, [cartDetails, selectedClient?.COD_CLIENTE, getComboCartItemInfo]);

  const getMultiple = useCallback(
    (clientCod: number | string, mountKey: string) => quantityByMountKey[`${clientCod}-${mountKey}`] || 0,
    [quantityByMountKey]
  );

  // Consumo total de FLEX (apenas DESCONTOS) em todos os clientes
  const discountAmount = useMemo(() => {
    if (!discounts) return 0;

    const paymentCodes = getOnStorage('paymentCode') || {};

    const totalDiscount = Object.keys(discounts).reduce((acc, clientCod) => {
      const client = discounts[Number(clientCod)];
      const codePayment = paymentCodes[Number(clientCod)];
      return (
        acc +
        Object.keys(client).reduce((accDiscount, uid) => {
          const entry: any = client[uid];
          const percentual = Number(entry?.percentual || 0);

          if (percentual >= 0) return accDiscount; // ignora acréscimos

          const finalPrice = Number(entry?.discountPrice?.[codePayment] || 0);
          if (!finalPrice) return accDiscount;

          const originalPrice = getOriginalValue(finalPrice, percentual);
          const multiple = getMultiple(clientCod, uid);

          const diff = (finalPrice - originalPrice) * multiple; // negativo
          return accDiscount + diff;
        }, 0)
      );
    }, 0);

    return Number(totalDiscount.toFixed(2));
  }, [discounts, getMultiple]);

  // Acréscimos (saldoAFaturar) do cliente atual (sempre positivo)
  // saldoAFaturar armazenado POR PACK -> multiplicar pela quantidade no carrinho
  const increasesClient = useMemo(() => {
    const clientCod = selectedClient?.COD_CLIENTE;
    if (!clientCod) return 0;
    const client = discounts?.[clientCod] || {};

    const totalInc = Object.keys(client).reduce((acc, uid) => {
      const entry: any = client[uid];
      const percentual = Number(entry?.percentual || 0);
      const saldoAFaturar = Number(entry?.saldoAFaturar || 0);
      const multiple = getMultiple(clientCod, uid);

      // Prioridade:
      // 1. Se saldoAFaturar > 0, usamos (saldoAFaturar * quantidade)
      // 2. Se não houver saldoAFaturar mas percentual > 0, calculamos acréscimo via percentual (fallback)
      if (saldoAFaturar > 0) {
        return acc + saldoAFaturar * multiple;
      }

      if (percentual > 0) {
        const finalPrice = Number(entry?.discountPrice?.[paymentCode] || 0);
        if (!finalPrice) return acc;
        // originalPrice = finalPrice / (1 + percentual/100)
        const fator = 1 + percentual / 100;
        if (fator <= 0) return acc;
        const originalPrice = finalPrice / fator;
        const diff = (finalPrice - originalPrice) * multiple;
        return acc + diff;
      }

      return acc;
    }, 0);

    return Number(totalInc.toFixed(2));
  }, [discounts, paymentCode, getMultiple, selectedClient?.COD_CLIENTE]);

  const getDiscountAmountByClient = useCallback(
    (clientCod: number) => {
      if (!clientCod || !discounts) return 0;
      const client = discounts?.[clientCod] || {};
      const totalDiscount = Object.keys(client).reduce((accDiscount, uid) => {
        const entry: any = client[uid];
        const percentual = Number(entry?.percentual || 0);
        if (percentual >= 0) return accDiscount;
        const finalPrice = Number(entry?.discountPrice?.[paymentCode] || 0);
        if (!finalPrice) return accDiscount;
        const originalPrice = getOriginalValue(finalPrice, percentual);
        const multiple = getMultiple(clientCod, uid);
        const diff = (finalPrice - originalPrice) * multiple;
        return accDiscount + diff;
      }, 0);
      return Number(totalDiscount.toFixed(2));
    },
    [discounts, paymentCode, getMultiple]
  );

  const discountAmountAll = useMemo(() => {
    return discountAmount;
  }, [discountAmount]);

  const discountAmountClient = useMemo(() => {
    if (!isBFSeller) return 0;
    const clientCod = selectedClient?.COD_CLIENTE;
    if (!clientCod) return 0;
    const client = discounts?.[clientCod] || {};

    const totalDiscount = Object.keys(client).reduce((accDiscount, uid) => {
      const entry: any = client[uid];
      const percentual = Number(entry?.percentual || 0);
      if (percentual >= 0) return accDiscount;

      const finalPrice = Number(entry?.discountPrice?.[paymentCode] || 0);
      if (!finalPrice) return accDiscount;

      const originalPrice = getOriginalValue(finalPrice, percentual);
      const multiple = getMultiple(clientCod, uid);
      const diff = (finalPrice - originalPrice) * multiple; // negativo
      return accDiscount + diff;
    }, 0);

    return Number(totalDiscount.toFixed(2));
  }, [selectedClient?.COD_CLIENTE, isBFSeller, discounts, getMultiple]);

  const estimatedSaldoFlex = useMemo(() => Number((baseSaldo + discountAmount).toFixed(2)), [baseSaldo, discountAmount]);

  const estimatedSaldoFlexClient = useMemo(
    () => Number((baseSaldo + discountAmountClient).toFixed(2)),
    [baseSaldo, discountAmountClient]
  );

  const totalFlexClient = useMemo(
    () => Number((discountAmountClient + increasesClient).toFixed(2)),
    [discountAmountClient, increasesClient]
  );

  return (
    <DiscountFlexAmountContext.Provider
      value={{
        discountAmount, // só descontos (negativo)
        discountAmountClient, // só descontos (negativo)
        getDiscountAmountByClient,
        increasesClient, // só acréscimos
        totalFlexClient, // descontos + acréscimos (para exibir no carrinho)
        discountAmountAll, // agregação geral
        estimatedSaldoFlex,
        estimatedSaldoFlexClient,
        // aliases
        currentSaldoFlex: estimatedSaldoFlex,
        currentSaldoFlexClient: estimatedSaldoFlexClient,
      }}
    >
      {children}
    </DiscountFlexAmountContext.Provider>
  );
};

const useDiscountAmountFlex = (): HookProps & {
  increasesClient: number;
  totalFlexClient: number;
  discountAmountAll: number;
} => {
  const ctx = useContext(DiscountFlexAmountContext);
  if (!ctx) throw new Error('Erro ao usar hook DiscountFlexAmount');
  return ctx;
};

export { DiscountFlexAmountProvider, useDiscountAmountFlex };
